/**
 * Production HTTP client — gateway-first, request-id, timeout, refresh.
 * Prefer this over hard-coded service ports.
 */
import axios from 'axios';

const configuredBase = (
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_GATEWAY ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_GATEWAY_URL ||
  ''
).replace(/\/$/, '');

/** Same origin in a production build so the public site calls its own gateway. */
export function resolveApiBase() {
  if (configuredBase) {
    if (typeof window !== 'undefined') {
      const pageHost = window.location.hostname;
      const onLoopback = pageHost === 'localhost' || pageHost === '127.0.0.1';
      try {
        const apiHost = new URL(configuredBase, window.location.origin).hostname;
        const apiIsLoopback = apiHost === 'localhost' || apiHost === '127.0.0.1';
        if (!onLoopback && apiIsLoopback) return '';
      } catch (err) {
        return '';
      }
    }
    return configuredBase;
  }
  if (process.env.NODE_ENV === 'development') return 'http://localhost:8081';
  return '';
}

function mintRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `fe-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createHttpClient(baseURL = resolveApiBase()) {
  // Bearer JWT in Authorization — do not send cookies cross-origin
  // (subscription/consultancy CORS lacks Allow-Credentials → browser blocks).
  const client = axios.create({
    baseURL,
    timeout: 20000,
    withCredentials: false,
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Interview CORS allow-headers omit this name, so a cross-origin dev UI
    // (port 3001 → gateway 8081) never receives the list, report, or plan.
    const apiBase = resolveApiBase();
    const sameOrigin = typeof window === 'undefined'
      || !apiBase
      || new URL(apiBase, window.location.origin).origin === window.location.origin;
    if (sameOrigin && !config.headers['X-Request-ID']) {
      config.headers['X-Request-ID'] = mintRequestId();
    }
    return config;
  });

  let refreshInFlight = null;

  function refreshAccessToken(refreshToken) {
    if (!refreshInFlight) {
      const refreshUrl = `${resolveApiBase()}/api/token/refresh/`;
      refreshInFlight = axios
        .post(refreshUrl, { refresh: refreshToken }, { timeout: 10000 })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  }

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      if (error.response?.status === 401 && original && !original._retry) {
        original._retry = true;
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          try {
            const { data } = await refreshAccessToken(refreshToken);
            localStorage.setItem('access_token', data.access);
            original.headers.Authorization = `Bearer ${data.access}`;
            return client.request(original);
          } catch (refreshError) {
            // Rate limits and a downstream 401 (for example a service that
            // does not share the JWT secret) must not wipe a valid session.
            const refreshStatus = refreshError.response?.status;
            if (refreshStatus === 429 || refreshStatus == null || refreshStatus >= 500) {
              return Promise.reject(error);
            }
            if (refreshStatus === 401 || refreshStatus === 403) {
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              const path = window.location.pathname;
              if (path !== '/login' && path !== '/admin/login') {
                window.location.href = path.startsWith('/admin')
                  ? '/admin/login'
                  : '/login';
              }
            }
          }
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const http = createHttpClient();
export default http;
export const API_GATEWAY = resolveApiBase();
